import { createContext, useCallback, useContext, useMemo, useState, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { PRIVANA_FALLBACK_LOCALE, normalizeLocale } from '../../shared/locales.mjs'
import { translate } from './index.js'

const PrivanaI18nContext = createContext(null)
const managedDocumentOwners = new Map()
let fallbackDocumentLocale = PRIVANA_FALLBACK_LOCALE
function publishDocumentLocale() {
  const active = [...managedDocumentOwners.values()].reduce((current, entry) => !current || entry.depth >= current.depth ? entry : current, null)
  document.documentElement.lang = active?.locale || fallbackDocumentLocale
}

export function PrivanaI18nProvider({ children, initialLocale = PRIVANA_FALLBACK_LOCALE, resolvedLocale, localeManaged = false, onLocaleChange }) {
  const parent = useContext(PrivanaI18nContext)
  const depth = (parent?._documentDepth || 0) + 1
  const [localLocale, setLocaleState] = useState(() => normalizeLocale(initialLocale))
  const locale = resolvedLocale === undefined ? localLocale : normalizeLocale(resolvedLocale)
  const documentOwner = useRef(Symbol('locale-owner'))
  useLayoutEffect(() => {
    if (localeManaged) {
      const owner = documentOwner.current
      managedDocumentOwners.set(owner, { depth, locale })
      publishDocumentLocale()
      return () => {
        managedDocumentOwners.delete(owner)
        publishDocumentLocale()
      }
    }
    fallbackDocumentLocale = locale
    // An outer bootstrap provider must not overwrite an authenticated surface's
    // language after its child layout effects have already applied a cache hit.
    publishDocumentLocale()
  }, [locale, localeManaged, depth])
  const setLocale = useCallback((nextLocale) => {
    if (onLocaleChange) onLocaleChange(normalizeLocale(nextLocale));
    else setLocaleState(normalizeLocale(nextLocale));
  }, [onLocaleChange])
  const t = useCallback((key, values) => translate(key, locale, values), [locale])
  const value = useMemo(() => ({ locale, setLocale, t, localeManaged, _documentDepth: depth }), [locale, setLocale, t, localeManaged, depth])
  return <PrivanaI18nContext.Provider value={value}>{children}</PrivanaI18nContext.Provider>
}

PrivanaI18nProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialLocale: PropTypes.string,
  resolvedLocale: PropTypes.string,
  localeManaged: PropTypes.bool,
  onLocaleChange: PropTypes.func,
}

export function usePrivanaI18n() {
  const context = useContext(PrivanaI18nContext)
  if (!context) throw new Error('usePrivanaI18n must be used within PrivanaI18nProvider')
  return context
}

export function useOptionalPrivanaI18n() {
  const context = useContext(PrivanaI18nContext)
  return context || { locale: PRIVANA_FALLBACK_LOCALE, setLocale: () => {}, t: (key, values) => translate(key, PRIVANA_FALLBACK_LOCALE, values) }
}

// Public surfaces can reuse the app provider while remaining renderable standalone.
export function useHasPrivanaI18n() {
  return useContext(PrivanaI18nContext) !== null;
}
